import * as React from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'
import { rgba } from 'polished'
import { ConfigUiHint, ConfigUiHints, JsonSchema } from '../../../types'
import { defaultValue, schemaType } from '../../../utils/hint-for-path'
import { SchemaForm } from '../SchemaForm'
import { TextField } from './TextField'
import { NumberField } from './NumberField'

interface ArrayFieldProps {
  label: string
  schema: JsonSchema
  value: unknown[]
  hints: ConfigUiHints
  path: Array<string | number>
  hint?: ConfigUiHint
  onChange: (path: Array<string | number>, value: unknown) => void
  disabled?: boolean
}

const Wrapper = styled.div`
  margin-bottom: 12px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`

const Label = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.white};
  font-family: 'NunitoSansSemiBold';
  flex: 1;
`

const ItemCount = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.listDesc};
`

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: none;
  border: 1px solid ${(props) => props.theme.accent};
  border-radius: ${(props) => props.theme.radius};
  color: ${(props) => props.theme.accent};
  font-size: 12px;
  padding: 4px 10px;
  cursor: pointer;
  min-height: 44px;
  min-width: 44px;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.accent};
    color: ${(props) => props.theme.white};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(props) => props.theme.accent};
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HelpText = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.gray};
  margin-bottom: 8px;
`

const EmptyState = styled.div`
  padding: 16px;
  text-align: center;
  color: ${(props) => props.theme.listDesc};
  font-size: 13px;
  border: 1px dashed ${(props) => props.theme.listDesc};
  border-radius: ${(props) => props.theme.radius};
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ItemCard = styled.div`
  background-color: ${(props) => props.theme.secondaryPanel};
  border-radius: ${(props) => props.theme.radius};
  overflow: hidden;
`

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid ${(props) => props.theme.primary};
`

const ItemIndex = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.listDesc};
  font-family: 'NunitoSansSemiBold';
`

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${(props) => props.theme.listDesc};
  font-size: 12px;
  padding: 2px 6px;
  cursor: pointer;
  border-radius: ${(props) => props.theme.radius};
  min-height: 44px;
  min-width: 44px;

  &:hover:not(:disabled) {
    color: ${(props) => props.theme.error};
    background-color: ${(props) => rgba(props.theme.error, 0.1)};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(props) => props.theme.accent};
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ItemContent = styled.div`
  padding: 12px;

  @media (max-width: ${screenSizes.XS}px) {
    padding: 8px;
  }
`

function resolveItemDefault(itemsSchema: JsonSchema, hint?: ConfigUiHint): unknown {
  if (hint?.itemTemplate !== undefined) return structuredClone(hint.itemTemplate)
  return defaultValue(itemsSchema)
}

function renderPrimitiveItem(
  itemsSchema: JsonSchema,
  item: unknown,
  index: number,
  path: Array<string | number>,
  hints: ConfigUiHints,
  onChange: (path: Array<string | number>, value: unknown) => void,
  arr: unknown[],
  disabled?: boolean
) {
  const type = schemaType(itemsSchema)

  if (type === 'number' || type === 'integer') {
    return (
      <NumberField
        label=""
        value={typeof item === 'number' ? item : 0}
        onChange={(v) => {
          const next = [...arr]
          next[index] = v
          onChange(path, next)
        }}
        schema={itemsSchema}
        disabled={disabled}
      />
    )
  }

  return (
    <TextField
      label=""
      value={item != null ? String(item) : ''}
      onChange={(v) => {
        const next = [...arr]
        next[index] = v
        onChange(path, next)
      }}
      schema={itemsSchema}
      disabled={disabled}
    />
  )
}

export function ArrayField({
  label,
  schema,
  value,
  hints,
  path,
  hint,
  onChange,
  disabled,
}: ArrayFieldProps) {
  const itemsSchema = Array.isArray(schema.items) ? schema.items[0] : schema.items
  const help = hint?.help ?? schema.description
  const arr = value ?? []
  const itemType = itemsSchema ? schemaType(itemsSchema) : undefined
  const isObjectItems = itemType === 'object'

  const handleAdd = () => {
    if (!itemsSchema) return
    const newItem = resolveItemDefault(itemsSchema, hint)
    onChange(path, [...arr, newItem])
  }

  const handleRemove = (index: number) => {
    const next = [...arr]
    next.splice(index, 1)
    onChange(path, next)
  }

  if (!itemsSchema) {
    return (
      <Wrapper>
        <Header>
          <Label>{label}</Label>
        </Header>
        <EmptyState>Unsupported array schema (no items definition).</EmptyState>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Header>
        <Label>{label}</Label>
        <ItemCount>
          {arr.length} item{arr.length !== 1 ? 's' : ''}
        </ItemCount>
        <AddButton type="button" disabled={disabled} onClick={handleAdd}>
          + Add
        </AddButton>
      </Header>
      {help && <HelpText>{help}</HelpText>}

      {arr.length === 0 ? (
        <EmptyState>No items yet. Click &quot;Add&quot; to create one.</EmptyState>
      ) : (
        <ItemList>
          {arr.map((item, idx) => (
            <ItemCard key={idx}>
              <ItemHeader>
                <ItemIndex>#{idx + 1}</ItemIndex>
                <RemoveButton
                  type="button"
                  title="Remove item"
                  disabled={disabled}
                  onClick={() => handleRemove(idx)}
                >
                  &#10005; Remove
                </RemoveButton>
              </ItemHeader>
              <ItemContent>
                {isObjectItems ? (
                  <SchemaForm
                    schema={itemsSchema}
                    value={
                      item && typeof item === 'object' && !Array.isArray(item)
                        ? (item as Record<string, unknown>)
                        : {}
                    }
                    hints={hints}
                    path={[...path, idx]}
                    onChange={onChange}
                    disabled={disabled}
                  />
                ) : (
                  renderPrimitiveItem(itemsSchema, item, idx, path, hints, onChange, arr, disabled)
                )}
              </ItemContent>
            </ItemCard>
          ))}
        </ItemList>
      )}
    </Wrapper>
  )
}
